import { useDocument } from "@/composables/useFirestore";
import { useI18n } from "@/composables/useI18n";
import { useOpenInNewTab } from "@/composables/useOpenInNewTab";
import { call, db } from "@/firebase";
import { useToast, useUserStore } from "@/store";
import type { Account } from "@aircap/common/account";
import type { CallablesMap } from "@aircap/common/callables";
import {
  DocumentTypeId,
  getTreezorKycEffectiveBeneficiaryDocumentRequirements,
  getTreezorKycReviewReadiness,
  isTreezorUserPep,
  RelationshipTypeId,
  TREEZOR_KYC_DELEGATED_COMPANY_REPRESENTATIVE_DOCUMENT_REQUIREMENTS,
  TREEZOR_KYC_REQUIRED_DOCUMENT_TYPE_IDS,
  TREEZOR_KYC_REQUIRED_EFFECTIVE_BENEFICIARY_FIELDS,
  TREEZOR_KYC_W9_DOCUMENT_TYPE_ID,
  TREEZOR_PEP_USER_TAG_SUFFIX,
  type PreReviewDocument,
  type Relationship,
  type TreezorKycEffectiveBeneficiaryDocumentRequirement,
  type TreezorKycEffectiveBeneficiaryField,
  type User as TreezorUser,
} from "@aircap/common/treezor";
import { captureException } from "@sentry/vue";
import { useIntervalFn, useNow } from "@vueuse/core";
import { collection, doc } from "firebase/firestore";
import {
  computed,
  inject,
  onBeforeUnmount,
  provide,
  reactive,
  ref,
  watch,
  type InjectionKey,
  type Ref,
} from "vue";

export const requiredDocumentLabelKeys = {
  [DocumentTypeId.LegalStatus]: "legalStatus",
} as const satisfies Record<(typeof TREEZOR_KYC_REQUIRED_DOCUMENT_TYPE_IDS)[number], string>;

export const requiredDocuments = TREEZOR_KYC_REQUIRED_DOCUMENT_TYPE_IDS.map((documentTypeId) => ({
  documentTypeId,
  labelKey: requiredDocumentLabelKeys[documentTypeId],
}));

export type RequiredDocumentTypeId = (typeof requiredDocuments)[number]["documentTypeId"];
export type FileInputValue = File | File[] | null;
export type EffectiveBeneficiaryDocumentRequirementId =
  TreezorKycEffectiveBeneficiaryDocumentRequirement["id"];
export type EffectiveBeneficiaryDocumentUploadState = {
  file: File | null;
  uploading: boolean;
  documentTypeId: DocumentTypeId | null;
};
export type DocumentLabelKey =
  | (typeof requiredDocuments)[number]["labelKey"]
  | "pepAssetsDeclaration"
  | "naturalRevenueAssetsReport"
  | "proofOfAddress"
  | "identityCard"
  | "residencePermit"
  | "passport"
  | "w9Form"
  | "companyRegistration"
  | "powerOfAttorney"
  | "other";

export const effectiveBeneficiaryDocumentRequirementCopy = {
  identity: {
    titleKey: "treezorOnboarding.effectiveBeneficiaries.identityDocument",
    hintKey: "treezorOnboarding.effectiveBeneficiaries.identityDocumentHint",
  },
  w9: {
    titleKey: "treezorOnboarding.documents.w9Form",
    hintKey: "treezorOnboarding.effectiveBeneficiaries.w9FormHint",
  },
  pepAssets: {
    titleKey: "treezorOnboarding.documents.pepAssetsDeclaration",
    hintKey: "treezorOnboarding.effectiveBeneficiaries.pepAssetsHint",
  },
  pepIncome: {
    titleKey: "treezorOnboarding.documents.naturalRevenueAssetsReport",
    hintKey: "treezorOnboarding.effectiveBeneficiaries.pepIncomeHint",
  },
  pepAddress: {
    titleKey: "treezorOnboarding.documents.proofOfAddress",
    hintKey: "treezorOnboarding.effectiveBeneficiaries.pepAddressHint",
  },
  legalRepresentativePowerOfAttorney: {
    titleKey: "treezorOnboarding.accountCreator.legalRepresentativePowerOfAttorney",
    hintKey: "treezorOnboarding.accountCreator.legalRepresentativePowerOfAttorneyHint",
  },
  legalRepresentativeProofOfPower: {
    titleKey: "treezorOnboarding.accountCreator.legalRepresentativeProofOfPower",
    hintKey: "treezorOnboarding.accountCreator.legalRepresentativeProofOfPowerHint",
  },
} as const satisfies Record<
  EffectiveBeneficiaryDocumentRequirementId,
  { titleKey: string; hintKey: string }
>;

export const isCompanyRepresentativeDocumentRequirement = (
  requirement: TreezorKycEffectiveBeneficiaryDocumentRequirement,
) =>
  TREEZOR_KYC_DELEGATED_COMPANY_REPRESENTATIVE_DOCUMENT_REQUIREMENTS.some(
    (roleDocumentRequirement) => roleDocumentRequirement.id === requirement.id,
  );

export const documentTypeLabelKey = (documentTypeId: PreReviewDocument["documentType"]) => {
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
  switch (documentTypeId) {
    case DocumentTypeId.IdentityCard:
      return "identityCard" as const;
    case DocumentTypeId.ResidencePermit:
      return "residencePermit" as const;
    case DocumentTypeId.Passport:
      return "passport" as const;
    case TREEZOR_KYC_W9_DOCUMENT_TYPE_ID:
      return "w9Form" as const;
    case DocumentTypeId.Kbis:
      return "companyRegistration" as const;
    case DocumentTypeId.LegalStatus:
      return "legalStatus" as const;
    case DocumentTypeId.PowerOfAttorney:
      return "powerOfAttorney" as const;
    case DocumentTypeId.ProofOfAddress:
      return "proofOfAddress" as const;
    case DocumentTypeId.PepAssetsDeclaration:
      return "pepAssetsDeclaration" as const;
    case DocumentTypeId.NaturalPersonRevenueAssetsDeclaration:
      return "naturalRevenueAssetsReport" as const;
    default:
      return "other" as const;
  }
};

export const effectiveBeneficiaryFieldLabelKey = (field: TreezorKycEffectiveBeneficiaryField) => {
  switch (field) {
    case "phone":
      return "common.phone" as const;
    case "specifiedUSPerson":
    case "firstname":
    case "lastname":
    case "birthday":
    case "placeOfBirth":
    case "birthCountry":
    case "nationality":
    case "occupationCategory":
    case "monthlyIncomeRange":
    case "personalAssetsRange":
      return `treezorOnboarding.effectiveBeneficiaries.${field}` as const;
    case "address1":
    case "postcode":
    case "city":
    case "country":
      return `treezorOnboarding.${field}` as const;
  }
};

/** Person fields that the "Vous" form edits, rather than the beneficiary dialog. */
const isAccountCreatorStepField = (
  userId: number,
  accountCreatorUserId: number | undefined,
  field: TreezorKycEffectiveBeneficiaryField,
) =>
  userId === accountCreatorUserId &&
  TREEZOR_KYC_REQUIRED_EFFECTIVE_BENEFICIARY_FIELDS.some(
    (accountCreatorField) => accountCreatorField === field,
  );

export const effectiveBeneficiaryStepIncomplete = (
  person: {
    userId: number;
    missingFields: readonly TreezorKycEffectiveBeneficiaryField[];
    missingTaxResidence: boolean;
    missingSharesPercent: boolean;
  },
  accountCreatorUserId: number | undefined,
) =>
  person.missingTaxResidence ||
  person.missingSharesPercent ||
  person.missingFields.some(
    (field) => !isAccountCreatorStepField(person.userId, accountCreatorUserId, field),
  );

const countryOrNull = (value: string | null | undefined) =>
  value && /^[A-Z]{2}$/.test(value) ? value : null;

const emptyAccountCreatorForm = () => ({
  legalRole:
    "companyRepresentative" as CallablesMap["updateTreezorAccountCreator"]["params"]["legalRole"],
  isEffectiveBeneficiary: false,
  specifiedUSPerson: false,
  firstname: "",
  lastname: "",
  middleNames: "",
  birthday: "",
  placeOfBirth: "",
  birthCountry: null as string | null,
  nationality: null as string | null,
  phone: "",
  address1: "",
  address2: "",
  address3: "",
  postcode: "",
  city: "",
  country: null as string | null,
});

export const accountCreatorFormFrom = (
  treezorUser: CallablesMap["getTreezorAccountCreator"]["response"]["user"],
  roles: {
    isLegalRepresentative: boolean;
    isEffectiveBeneficiary: boolean;
  },
) => {
  let legalRole: ReturnType<typeof emptyAccountCreatorForm>["legalRole"] = "companyRepresentative";
  if (roles.isLegalRepresentative) legalRole = "legalRepresentative";
  return {
    legalRole,
    isEffectiveBeneficiary: roles.isEffectiveBeneficiary,
    specifiedUSPerson: treezorUser.specifiedUSPerson === 1,
    firstname: treezorUser.firstname,
    lastname: treezorUser.lastname,
    middleNames: treezorUser.middleNames,
    birthday: treezorUser.birthday ?? "",
    placeOfBirth: treezorUser.placeOfBirth,
    birthCountry: countryOrNull(treezorUser.birthCountry),
    nationality: countryOrNull(treezorUser.nationality),
    phone: treezorUser.phone,
    address1: treezorUser.address1,
    address2: treezorUser.address2,
    address3: treezorUser.address3 ?? "",
    postcode: treezorUser.postcode,
    city: treezorUser.city,
    country: countryOrNull(treezorUser.country),
  };
};

export const emptyEffectiveBeneficiaryForm = () => ({
  specifiedUSPerson: false,
  firstname: "",
  lastname: "",
  middleNames: "",
  birthday: "",
  placeOfBirth: "",
  birthCountry: null as string | null,
  nationality: null as string | null,
  phone: "",
  address1: "",
  address2: "",
  address3: "",
  postcode: "",
  city: "",
  country: null as string | null,
  relationshipTypeIds: [
    RelationshipTypeId.EffectiveBeneficiaryShares,
  ] as Relationship["relationshipTypeId"][],
  controllingPersonType: null as 1 | 2 | null,
  sharesPercent: "",
  taxResidenceCountry: null as string | null,
  taxPayerId: "",
  isPep: false,
  occupationCategory: null as TreezorUser["occupationCategory"],
  monthlyIncomeRange: null as TreezorUser["monthlyIncomeRange"],
  personalAssetsRange: null as TreezorUser["personalAssetsRange"],
});

export const effectiveBeneficiaryFormFrom = (
  effectiveBeneficiary: CallablesMap["getTreezorEffectiveBeneficiaries"]["response"][number],
) => {
  const sharesPercent =
    effectiveBeneficiary.relationships.find(
      (relationship) =>
        relationship.relationshipTypeId === RelationshipTypeId.EffectiveBeneficiaryShares,
    )?.value ?? "";
  let controllingPersonType: 1 | 2 | null = sharesPercent ? 1 : null;
  switch (effectiveBeneficiary.user.controllingPersonType) {
    case 0:
      break;
    case 1:
      controllingPersonType = 1;
      break;
    case 2:
    case 3:
      controllingPersonType = 2;
      break;
  }
  return {
    specifiedUSPerson: effectiveBeneficiary.user.specifiedUSPerson === 1,
    firstname: effectiveBeneficiary.user.firstname,
    lastname: effectiveBeneficiary.user.lastname,
    middleNames: effectiveBeneficiary.user.middleNames,
    birthday: effectiveBeneficiary.user.birthday ?? "",
    placeOfBirth: effectiveBeneficiary.user.placeOfBirth,
    birthCountry: countryOrNull(effectiveBeneficiary.user.birthCountry),
    nationality: countryOrNull(effectiveBeneficiary.user.nationality),
    phone: effectiveBeneficiary.user.phone,
    address1: effectiveBeneficiary.user.address1,
    address2: effectiveBeneficiary.user.address2,
    address3: effectiveBeneficiary.user.address3 ?? "",
    postcode: effectiveBeneficiary.user.postcode,
    city: effectiveBeneficiary.user.city,
    country: countryOrNull(effectiveBeneficiary.user.country),
    relationshipTypeIds: effectiveBeneficiary.relationships
      .map((relationship) => relationship.relationshipTypeId)
      .filter(
        (relationshipTypeId) => relationshipTypeId !== RelationshipTypeId.CompanyRepresentative,
      ),
    controllingPersonType,
    sharesPercent,
    taxResidenceCountry: countryOrNull(effectiveBeneficiary.taxResidences[0]?.country),
    taxPayerId: effectiveBeneficiary.taxResidences[0]?.taxPayerId ?? "",
    isPep: isTreezorUserPep(effectiveBeneficiary.user.userTag),
    occupationCategory: effectiveBeneficiary.user.occupationCategory ?? null,
    monthlyIncomeRange: effectiveBeneficiary.user.monthlyIncomeRange ?? null,
    personalAssetsRange: effectiveBeneficiary.user.personalAssetsRange ?? null,
  };
};

export const effectiveBeneficiaryDocumentRequirementsForForm = (
  form: ReturnType<typeof emptyEffectiveBeneficiaryForm>,
  isAccountCreator: boolean,
) =>
  getTreezorKycEffectiveBeneficiaryDocumentRequirements({
    relationshipTypeIds: form.relationshipTypeIds,
    user: {
      country: form.country,
      specifiedUSPerson: form.specifiedUSPerson,
      userTag: form.isPep ? TREEZOR_PEP_USER_TAG_SUFFIX : null,
    },
    taxResidences: form.taxResidenceCountry
      ? [{ country: form.taxResidenceCountry, isDeleted: false }]
      : [],
    isAccountCreator,
  });

/** Withdrawn ISO 3166 aliases and CLDR macro-regions / pseudo-regions that are not countries. */
const obsoleteIsoCountryCodes = new Set([
  "EU",
  "EZ",
  "QO",
  "UN",
  "XA",
  "XB",
  "ZZ",
  "AN",
  "BU",
  "CS",
  "DD",
  "DY",
  "FX",
  "HV",
  "NH",
  "RH",
  "SU",
  "TP",
  "UK",
  "VD",
  "YD",
  "YU",
  "ZR",
]);

const createTreezorOnboarding = (accountId: Readonly<Ref<string>>) => {
  const { t, locale } = useI18n();
  const { showSuccessToast, showErrorToast } = useToast();
  const openInNewTab = useOpenInNewTab();
  const user = useUserStore();
  const canManage = computed(() => user.isKind("admin") || user.isKind("user"));

  const accountSource = computed(() => doc(collection<Account>(db, "accounts"), accountId.value));
  const { data: account, pending: loadingAccount } = useDocument<Account>(accountSource);

  const treezorDocuments = ref<PreReviewDocument[]>([]);
  const loadingDocuments = ref(true);
  const documentsLoadFailed = ref(false);
  const effectiveBeneficiaries = ref<CallablesMap["getTreezorEffectiveBeneficiaries"]["response"]>(
    [],
  );
  const loadingEffectiveBeneficiaries = ref(true);
  const effectiveBeneficiariesLoadFailed = ref(false);
  const accountCreator = ref<CallablesMap["getTreezorAccountCreator"]["response"]["user"] | null>(
    null,
  );
  const accountCreatorRelationship =
    ref<CallablesMap["getTreezorAccountCreator"]["response"]["relationship"]>(null);
  const accountCreatorKycLivenessStatus =
    ref<CallablesMap["getTreezorAccountCreator"]["response"]["kycLivenessStatus"]>("none");
  const accountCreatorKycLivenessReasonCodes = ref<
    CallablesMap["getTreezorAccountCreator"]["response"]["kycLivenessReasonCodes"]
  >([]);
  const accountCreatorKycLivenessStartedAt = ref<number | null>(null);
  let kycLivenessServerStartedAt: number | null = null;
  const now = useNow({ scheduler: (update) => useIntervalFn(update, 1000) });
  const accountCreatorCanStartKycLiveness = computed(
    () =>
      accountCreatorKycLivenessStartedAt.value === null ||
      now.value.getTime() - accountCreatorKycLivenessStartedAt.value >= 60 * 1000,
  );
  const accountCreatorHasPassedKycLiveness = computed(
    () => accountCreatorKycLivenessStatus.value === "verified",
  );
  const loadingAccountCreator = ref(true);

  const updatingAccountCreator = ref(false);
  const creatingEffectiveBeneficiary = ref(false);
  const updatingEffectiveBeneficiaryId = ref<number>();
  const deletingEffectiveBeneficiaryId = ref<number>();

  const effectiveBeneficiaryDocumentState = ref<
    Record<
      number,
      Partial<
        Record<EffectiveBeneficiaryDocumentRequirementId, EffectiveBeneficiaryDocumentUploadState>
      >
    >
  >({});
  const effectiveBeneficiaryForms = ref<
    Record<number, ReturnType<typeof emptyEffectiveBeneficiaryForm>>
  >({});
  const accountCreatorFormData = reactive(emptyAccountCreatorForm());

  const effectiveBeneficiariesBusy = computed(
    () =>
      creatingEffectiveBeneficiary.value ||
      updatingEffectiveBeneficiaryId.value !== undefined ||
      deletingEffectiveBeneficiaryId.value !== undefined ||
      Object.values(effectiveBeneficiaryDocumentState.value).some((uploads) =>
        Object.values(uploads).some((state) => state?.uploading),
      ),
  );

  const accountCreatorRelatedBusy = computed(() => {
    const userId = accountCreator.value?.userId;
    return (
      updatingAccountCreator.value ||
      loadingAccountCreator.value ||
      loadingEffectiveBeneficiaries.value ||
      (userId !== undefined &&
        (updatingEffectiveBeneficiaryId.value === userId ||
          deletingEffectiveBeneficiaryId.value === userId))
    );
  });

  const kycEffectiveBeneficiaries = computed(() =>
    effectiveBeneficiaries.value.map((effectiveBeneficiary) => ({
      userId: effectiveBeneficiary.user.userId,
      relationships: effectiveBeneficiary.relationships,
      user: effectiveBeneficiary.user,
      documents: effectiveBeneficiary.documents,
      taxResidences: effectiveBeneficiary.taxResidences,
    })),
  );

  const kycReviewReadiness = computed(() =>
    getTreezorKycReviewReadiness({
      company: account.value?.treezorCompany,
      documents: treezorDocuments.value,
      effectiveBeneficiaries: kycEffectiveBeneficiaries.value,
      accountCreatorUserId: accountCreator.value?.userId,
      accountCreatorHasRoleRelationship: !!accountCreatorRelationship.value,
      accountCreatorHasKycLiveness: accountCreatorHasPassedKycLiveness.value,
    }),
  );

  const kycSubmitReadiness = computed(() =>
    getTreezorKycReviewReadiness({
      company: account.value?.treezorCompany,
      documents: treezorDocuments.value,
      effectiveBeneficiaries: kycEffectiveBeneficiaries.value,
      accountCreatorUserId: accountCreator.value?.userId,
      accountCreatorHasRoleRelationship: !!accountCreatorRelationship.value,
      accountCreatorHasKycLiveness: accountCreatorHasPassedKycLiveness.value,
      requireValidatedDocuments: true,
    }),
  );

  const submittedForReview = computed(() => kycReviewReadiness.value.alreadySubmitted);
  const kycReadyForAdminReview = computed(
    () => account.value?.kycAdminReview?.status === "pending",
  );
  const canEditKyc = computed(
    () =>
      !submittedForReview.value &&
      (user.isKind("admin") || (user.isKind("user") && !kycReadyForAdminReview.value)),
  );

  // Steps 1 to 5 split `kycReviewReadiness` with no overlap: all five are complete iff
  // `dataComplete`. "Vous" owns the creator's role relationship and the identity fields of
  // its form; "Vérification d'identité" owns liveness; "Bénéficiaires" owns everything else
  // about people except documents.
  const companyInfoComplete = computed(
    () => kycReviewReadiness.value.missingCompanyFields.length === 0,
  );
  const accountCreatorMissingFields = computed(() =>
    kycReviewReadiness.value.incompleteEffectiveBeneficiaries.flatMap((incompletePerson) =>
      incompletePerson.missingFields.filter((field) =>
        isAccountCreatorStepField(incompletePerson.userId, accountCreator.value?.userId, field),
      ),
    ),
  );
  const accountCreatorComplete = computed(
    () =>
      !kycReviewReadiness.value.missingAccountCreatorRelationship &&
      accountCreatorMissingFields.value.length === 0,
  );
  const identityVerificationComplete = computed(
    () => !kycReviewReadiness.value.missingAccountCreatorKycLiveness,
  );
  const effectiveBeneficiariesComplete = computed(
    () =>
      kycReviewReadiness.value.missingRelationshipTypeIds.length === 0 &&
      kycReviewReadiness.value.incompleteEffectiveBeneficiaries.every(
        (incompletePerson) =>
          !effectiveBeneficiaryStepIncomplete(incompletePerson, accountCreator.value?.userId),
      ),
  );
  // Beneficiaries saved in this visit: their « Informations manquantes » chip turns red, and so
  // does the "Bénéficiaires" step while one of them is still incomplete.
  const checkedBeneficiaryIds = reactive(new Set<number>());
  const checkedBeneficiaryIncomplete = computed(() =>
    kycReviewReadiness.value.incompleteEffectiveBeneficiaries.some(
      (incompletePerson) =>
        checkedBeneficiaryIds.has(incompletePerson.userId) &&
        effectiveBeneficiaryStepIncomplete(incompletePerson, accountCreator.value?.userId),
    ),
  );
  const fileComplete = computed(
    () =>
      !loadingAccount.value &&
      !loadingDocuments.value &&
      !loadingEffectiveBeneficiaries.value &&
      !documentsLoadFailed.value &&
      !effectiveBeneficiariesLoadFailed.value &&
      kycReviewReadiness.value.missingDocumentTypeIds.length === 0 &&
      kycReviewReadiness.value.incompleteEffectiveBeneficiaries.every(
        (incompletePerson) => incompletePerson.missingDocumentRequirements.length === 0,
      ),
  );

  const accountCreatorDocumentRequirements = computed(() => {
    const userId = accountCreator.value?.userId;
    if (userId === undefined) return [];
    const saved = effectiveBeneficiaries.value.find(
      (effectiveBeneficiary) => effectiveBeneficiary.user.userId === userId,
    );
    const relationshipTypeIds = (
      saved?.relationships ??
      (accountCreatorRelationship.value ? [accountCreatorRelationship.value] : [])
    )
      .map((relationship) => relationship.relationshipTypeId)
      .filter(
        (relationshipTypeId) =>
          relationshipTypeId === RelationshipTypeId.CompanyRepresentative ||
          relationshipTypeId === RelationshipTypeId.LegalRepresentative,
      );
    if (!relationshipTypeIds.includes(RelationshipTypeId.CompanyRepresentative)) return [];
    return getTreezorKycEffectiveBeneficiaryDocumentRequirements({
      relationshipTypeIds,
      isAccountCreator: true,
    });
  });

  const syncEffectiveBeneficiaryDocumentUploads = () => {
    effectiveBeneficiaryDocumentState.value = Object.fromEntries(
      effectiveBeneficiaries.value.flatMap((effectiveBeneficiary) => {
        const form = effectiveBeneficiaryForms.value[effectiveBeneficiary.user.userId];
        if (!form) return [];
        const previous =
          effectiveBeneficiaryDocumentState.value[effectiveBeneficiary.user.userId] ?? {};
        const isAccountCreator = effectiveBeneficiary.user.userId === accountCreator.value?.userId;
        const requirements = [
          ...effectiveBeneficiaryDocumentRequirementsForForm(form, isAccountCreator),
          ...(isAccountCreator ? accountCreatorDocumentRequirements.value : []),
        ];
        return [
          [
            effectiveBeneficiary.user.userId,
            Object.fromEntries(
              requirements.map((requirement) => [
                requirement.id,
                previous[requirement.id] ?? {
                  file: null,
                  uploading: false,
                  // Several accepted types: the user picks one before choosing a file.
                  documentTypeId: requirement.anyOf.length === 1 ? requirement.anyOf[0] : null,
                },
              ]),
            ),
          ],
        ];
      }),
    );
  };

  const loadDocuments = async ({ silent = false } = {}) => {
    documentsLoadFailed.value = false;
    if (!canManage.value) {
      loadingDocuments.value = false;
      return;
    }
    if (!silent) loadingDocuments.value = true;
    try {
      treezorDocuments.value = (
        await call("getTreezorDocumentsForAccount")({
          accountId: accountId.value,
        })
      )
        .slice()
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    } catch (cause) {
      documentsLoadFailed.value = true;
      const error = new Error("Error loading Treezor documents", { cause });
      console.error(error);
      captureException(error);
      showErrorToast(cause instanceof Error ? cause.message : undefined);
    } finally {
      if (!silent) loadingDocuments.value = false;
    }
  };

  const loadEffectiveBeneficiaries = async ({
    silent = false,
    replaceUserIds = [],
  }: { silent?: boolean; replaceUserIds?: number[] } = {}) => {
    effectiveBeneficiariesLoadFailed.value = false;
    if (!canManage.value) {
      loadingEffectiveBeneficiaries.value = false;
      return;
    }
    if (!silent) loadingEffectiveBeneficiaries.value = true;
    try {
      const items = await call("getTreezorEffectiveBeneficiaries")({
        accountId: accountId.value,
      });
      effectiveBeneficiaryForms.value = Object.fromEntries(
        items.map((effectiveBeneficiary) => [
          effectiveBeneficiary.user.userId,
          replaceUserIds.includes(effectiveBeneficiary.user.userId)
            ? effectiveBeneficiaryFormFrom(effectiveBeneficiary)
            : (effectiveBeneficiaryForms.value[effectiveBeneficiary.user.userId] ??
              effectiveBeneficiaryFormFrom(effectiveBeneficiary)),
        ]),
      );
      effectiveBeneficiaries.value = items;
      syncEffectiveBeneficiaryDocumentUploads();
    } catch (cause) {
      effectiveBeneficiariesLoadFailed.value = true;
      const error = new Error("Error loading Treezor effective beneficiaries", { cause });
      console.error(error);
      captureException(error);
      showErrorToast(cause instanceof Error ? cause.message : undefined);
    } finally {
      if (!silent) loadingEffectiveBeneficiaries.value = false;
    }
  };

  const loadAccountCreator = async ({ silent = false } = {}) => {
    if (!canManage.value) {
      loadingAccountCreator.value = false;
      return;
    }
    if (!silent) loadingAccountCreator.value = true;
    try {
      const result = await call("getTreezorAccountCreator")({
        accountId: accountId.value,
      });
      accountCreator.value = result.user;
      accountCreatorRelationship.value = result.relationship;
      accountCreatorKycLivenessStatus.value = result.kycLivenessStatus;
      accountCreatorKycLivenessReasonCodes.value = result.kycLivenessReasonCodes;
      accountCreatorKycLivenessStartedAt.value = result.kycLivenessStartedAt;
      kycLivenessServerStartedAt = result.kycLivenessStartedAt;
      Object.assign(accountCreatorFormData, accountCreatorFormFrom(result.user, result));
    } catch (cause) {
      const error = new Error("Error loading Treezor account creator", { cause });
      console.error(error);
      captureException(error);
      showErrorToast(cause instanceof Error ? cause.message : undefined);
    } finally {
      if (!silent) loadingAccountCreator.value = false;
    }
  };

  watch(
    () => [accountId.value, canManage.value] as const,
    () => {
      void loadDocuments();
      void loadEffectiveBeneficiaries();
      void loadAccountCreator();
    },
    { immediate: true },
  );

  watch(
    () => [
      accountCreator.value?.userId,
      accountCreatorRelationship.value?.relationshipTypeId,
      ...effectiveBeneficiaries.value.map((effectiveBeneficiary) => {
        const form = effectiveBeneficiaryForms.value[effectiveBeneficiary.user.userId];
        return [
          effectiveBeneficiary.user.userId,
          form?.relationshipTypeIds.slice().sort().join(","),
          form?.country,
          form?.specifiedUSPerson,
          form?.taxResidenceCountry,
          form?.isPep,
        ] as const;
      }),
    ],
    syncEffectiveBeneficiaryDocumentUploads,
  );

  const isoCountryItems = computed(() => {
    const displayNames = new Intl.DisplayNames([locale.value], { type: "region" });
    const items: { title: string; value: string }[] = [];
    const seenTitles = new Set<string>();
    for (let first = 65; first <= 90; first++) {
      for (let second = 65; second <= 90; second++) {
        const code = String.fromCharCode(first, second);
        if (obsoleteIsoCountryCodes.has(code)) continue;
        const title = displayNames.of(code);
        if (!title || title === code || seenTitles.has(title)) continue;
        seenTitles.add(title);
        items.push({ title, value: code });
      }
    }
    return items.sort((a, b) => a.title.localeCompare(b.title));
  });

  const uploadTreezorDocumentFile = async ({
    file,
    documentTypeId,
    userId,
  }: {
    file: File;
    documentTypeId: DocumentTypeId;
    userId?: number;
  }) => {
    let fileExtension = file.name.split(".").pop()?.toLowerCase();
    if (fileExtension === "jpeg") fileExtension = "jpg";
    if (fileExtension === "tiff") fileExtension = "tif";
    if (
      fileExtension !== "jpg" &&
      fileExtension !== "png" &&
      fileExtension !== "svg" &&
      fileExtension !== "tif" &&
      fileExtension !== "pdf"
    ) {
      showErrorToast(t("treezorOnboarding.documents.invalidFileType"));
      return;
    }

    const upload = userId
      ? await call("createTreezorDocumentUploadForUser")({
          accountId: accountId.value,
          userId,
          documentTypeId,
        })
      : await call("createTreezorDocumentUploadForAccount")({
          accountId: accountId.value,
          documentTypeId,
        });

    const body = new FormData();
    for (const [key, value] of Object.entries(upload.formFields)) {
      if (typeof value !== "string" && typeof value !== "number" && typeof value !== "boolean") {
        throw new Error(`Unexpected upload form field type for ${key}`);
      }
      body.append(key, value.toString());
    }
    body.append("file", file);

    // Treezor's S3 bucket returns 204 without Access-Control-Allow-Origin, so a
    // normal CORS fetch throws even when the upload succeeded. `no-cors` still
    // sends the multipart POST and yields an opaque response we treat as success.
    const uploadResponse = await fetch(upload.form.action, {
      method: upload.form.method,
      body,
      mode: "no-cors",
    });
    if (uploadResponse.type !== "opaque" && !uploadResponse.ok) {
      throw new Error(`Document upload failed with status ${uploadResponse.status}`);
    }
    return upload.documentId;
  };

  const showDeleteDialog = ref(false);
  const documentToDelete = ref<{
    document: PreReviewDocument;
    labelKey: DocumentLabelKey;
    userId?: number;
  } | null>(null);
  const showReviewDialog = ref(false);
  const documentToReview = ref<{
    document: PreReviewDocument;
    labelKey: DocumentLabelKey;
    userId?: number;
  } | null>(null);
  const reviewComment = ref("");
  const deletingDocumentId = ref<string>();
  const reviewingDocumentId = ref<string>();
  const reviewingStatus = ref<"VALIDATED" | "REFUSED">();

  const onViewDocument = (document: PreReviewDocument, userId?: number) => {
    if (!canManage.value) return;

    if (userId)
      openInNewTab("treezorUserDocument", {
        accountId: accountId.value,
        documentId: document.documentId,
        userId,
      });
    else
      openInNewTab("treezorAccountDocument", {
        accountId: accountId.value,
        documentId: document.documentId,
      });
  };

  const openReviewConfirm = (
    document: PreReviewDocument,
    labelKey: DocumentLabelKey,
    userId?: number,
  ) => {
    if (!user.isKind("admin")) return;
    documentToReview.value = { document, labelKey, userId };
    reviewComment.value = "";
    showReviewDialog.value = true;
  };

  const onConfirmReviewDocument = async (status: "VALIDATED" | "REFUSED") => {
    if (!user.isKind("admin")) return;
    const pendingReview = documentToReview.value;
    if (!pendingReview) return;

    reviewingDocumentId.value = pendingReview.document.documentId;
    reviewingStatus.value = status;
    try {
      if (pendingReview.userId === undefined) {
        await call("reviewTreezorDocumentForAccount")({
          accountId: accountId.value,
          documentId: pendingReview.document.documentId,
          comment: reviewComment.value,
          status,
        });
        await loadDocuments({ silent: true });
      } else {
        await call("reviewTreezorDocumentForUser")({
          accountId: accountId.value,
          documentId: pendingReview.document.documentId,
          userId: pendingReview.userId,
          comment: reviewComment.value,
          status,
        });
        await loadEffectiveBeneficiaries({ silent: true });
      }
      showReviewDialog.value = false;
      documentToReview.value = null;
      reviewComment.value = "";
      showSuccessToast(t("treezorOnboarding.documents.reviewSuccess"));
    } catch (cause) {
      const error = new Error("Error reviewing Treezor document", { cause });
      console.error(error);
      captureException(error);
      showErrorToast(cause instanceof Error ? cause.message : undefined);
    } finally {
      reviewingDocumentId.value = undefined;
      reviewingStatus.value = undefined;
    }
  };

  const openDeleteConfirm = (
    document: PreReviewDocument,
    labelKey: DocumentLabelKey,
    userId?: number,
  ) => {
    if (!canEditKyc.value) return;
    documentToDelete.value = { document, labelKey, userId };
    showDeleteDialog.value = true;
  };

  const onConfirmDeleteDocument = async () => {
    if (!canEditKyc.value) return;
    const pendingDelete = documentToDelete.value;
    if (!pendingDelete) return;

    deletingDocumentId.value = pendingDelete.document.documentId;
    try {
      if (pendingDelete.userId === undefined) {
        await call("deleteTreezorDocumentForAccount")({
          accountId: accountId.value,
          documentId: pendingDelete.document.documentId,
        });
        await loadDocuments({ silent: true });
      } else {
        await call("deleteTreezorDocumentForUser")({
          accountId: accountId.value,
          documentId: pendingDelete.document.documentId,
          userId: pendingDelete.userId,
        });
        await loadEffectiveBeneficiaries({ silent: true });
      }
      showDeleteDialog.value = false;
      documentToDelete.value = null;
      showSuccessToast(t("treezorOnboarding.documents.deleteSuccess"));
    } catch (cause) {
      const error = new Error("Error deleting Treezor document", { cause });
      console.error(error);
      captureException(error);
      showErrorToast(cause instanceof Error ? cause.message : undefined);
    } finally {
      deletingDocumentId.value = undefined;
    }
  };

  let kycLivenessPollingTimer: ReturnType<typeof setInterval> | undefined;
  let kycLivenessRequestedAt: number | undefined;
  let kycLivenessPreviousStartedAt: number | null = null;
  let kycLivenessPollGeneration = 0;
  const stopKycLivenessPolling = () => {
    kycLivenessPollGeneration += 1;
    clearInterval(kycLivenessPollingTimer);
    kycLivenessPollingTimer = undefined;
  };
  onBeforeUnmount(stopKycLivenessPolling);
  watch(accountCreatorKycLivenessStatus, (status) => {
    if (status !== "started") {
      stopKycLivenessPolling();
      return;
    }
    if (kycLivenessPollingTimer) return;

    const pollingStartedAt = Date.now();
    kycLivenessPollingTimer = setInterval(() => {
      if (Date.now() - pollingStartedAt >= 60 * 60 * 1000) {
        accountCreatorKycLivenessStatus.value = "none";
        return;
      }
      const pollGeneration = kycLivenessPollGeneration;
      call("getTreezorAccountCreator")({ accountId: accountId.value })
        .then((result) => {
          if (pollGeneration !== kycLivenessPollGeneration) return;
          const awaitingNewAttempt =
            kycLivenessRequestedAt !== undefined &&
            Date.now() - kycLivenessRequestedAt < 2 * 60 * 1000 &&
            result.kycLivenessStatus !== "verified" &&
            result.kycLivenessStartedAt === kycLivenessPreviousStartedAt;
          if (awaitingNewAttempt) return;
          kycLivenessRequestedAt = undefined;
          kycLivenessServerStartedAt = result.kycLivenessStartedAt;
          accountCreatorKycLivenessStartedAt.value = result.kycLivenessStartedAt;
          accountCreatorKycLivenessStatus.value = result.kycLivenessStatus;
          accountCreatorKycLivenessReasonCodes.value = result.kycLivenessReasonCodes;
        })
        .catch((cause: unknown) => {
          const error = new Error("Error polling Treezor KYC liveness status", { cause });
          console.error(error);
          captureException(error);
        });
    }, 10_000);
  });
  watch(accountId, () => {
    checkedBeneficiaryIds.clear();
  });

  const onStartKycLiveness = () => {
    if (!canEditKyc.value || !user.isKind("user")) return;

    if (!openInNewTab("treezorKycLiveness", { accountId: accountId.value })) return;
    kycLivenessPollGeneration += 1;
    kycLivenessRequestedAt = Date.now();
    kycLivenessPreviousStartedAt = kycLivenessServerStartedAt;
    accountCreatorKycLivenessStartedAt.value = kycLivenessRequestedAt;
    accountCreatorKycLivenessStatus.value = "started";
    accountCreatorKycLivenessReasonCodes.value = [];
  };

  return {
    accountId,
    canManage,
    canEditKyc,
    account,
    loadingAccount,
    treezorDocuments,
    loadingDocuments,
    effectiveBeneficiaries,
    loadingEffectiveBeneficiaries,
    accountCreator,
    accountCreatorRelationship,
    accountCreatorHasPassedKycLiveness,
    accountCreatorKycLivenessStatus,
    accountCreatorKycLivenessReasonCodes,
    accountCreatorCanStartKycLiveness,
    loadingAccountCreator,
    updatingAccountCreator,
    creatingEffectiveBeneficiary,
    updatingEffectiveBeneficiaryId,
    deletingEffectiveBeneficiaryId,
    effectiveBeneficiaryDocumentState,
    effectiveBeneficiaryForms,
    accountCreatorFormData,
    accountCreatorDocumentRequirements,
    effectiveBeneficiariesBusy,
    accountCreatorRelatedBusy,
    kycEffectiveBeneficiaries,
    kycReviewReadiness,
    kycSubmitReadiness,
    submittedForReview,
    kycReadyForAdminReview,
    companyInfoComplete,
    accountCreatorMissingFields,
    accountCreatorComplete,
    identityVerificationComplete,
    effectiveBeneficiariesComplete,
    checkedBeneficiaryIds,
    checkedBeneficiaryIncomplete,
    fileComplete,
    loadDocuments,
    loadEffectiveBeneficiaries,
    isoCountryItems,
    uploadTreezorDocumentFile,
    showDeleteDialog,
    documentToDelete,
    showReviewDialog,
    documentToReview,
    reviewComment,
    deletingDocumentId,
    reviewingDocumentId,
    reviewingStatus,
    onViewDocument,
    openReviewConfirm,
    onConfirmReviewDocument,
    openDeleteConfirm,
    onConfirmDeleteDocument,
    onStartKycLiveness,
  };
};

const treezorOnboardingKey: InjectionKey<ReturnType<typeof createTreezorOnboarding>> =
  Symbol("treezorOnboarding");

export const provideTreezorOnboarding = (accountId: Readonly<Ref<string>>) => {
  const onboarding = createTreezorOnboarding(accountId);
  provide(treezorOnboardingKey, onboarding);
  return onboarding;
};

export const useTreezorOnboarding = () => {
  const onboarding = inject(treezorOnboardingKey);
  if (!onboarding) throw new Error("useTreezorOnboarding requires provideTreezorOnboarding");
  return onboarding;
};
